import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import colors from '../../styles/colors';
import type { Card, CardRarity } from '../../types/models/card';
import CardDetailModal from '../../pages/Cards/CardDetailModal';

// ─── Rarity config ────────────────────────────────────────────────────────────

const RARITY: Record<CardRarity, { label: string; color: string; glow: string }> = {
    legendary: { label: 'Legendary', color: '#f0c040', glow: 'rgba(240,192,64,0.35)' },
    epic:      { label: 'Epic',      color: '#a855f7', glow: 'rgba(168,85,247,0.35)' },
    rare:      { label: 'Rare',      color: '#3b82f6', glow: 'rgba(59,130,246,0.35)' },
    common:    { label: 'Common',    color: '#9ca3af', glow: 'rgba(156,163,175,0.2)' },
};

// ─── Styled components ────────────────────────────────────────────────────────

const Section = styled.section`
    width: 80%;
    margin: 8px auto 32px auto;

    @media (max-width: 768px) {
        width: 95%;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;

    h2 {
        font-size: 20px;
        font-weight: 700;
        color: white;
        text-transform: uppercase;
        letter-spacing: 1px;
        white-space: nowrap;
        margin: 0;
    }

    &::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(to right, rgba(163,27,27,0.6), transparent);
    }
`;

const Grid = styled.div`
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 16px;

    & > * {
        width: 210px;

        @media (max-width: 480px) {
            width: 140px;
        }
    }
`;

const shimmer = keyframes`
    0%   { background-position: 200% 0; }
    100% { background-position: -200% 0; }
`;

const SkeletonCard = styled.div`
    border-radius: 10px;
    height: 260px;
    background: linear-gradient(90deg, #1f1f1f 25%, #2a2a2a 50%, #1f1f1f 75%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite linear;
`;

const CardItem = styled.div<{ $rarity: CardRarity }>`
    position: relative;
    border-radius: 10px;
    overflow: hidden;
    cursor: pointer;
    border: 1px solid ${p => RARITY[p.$rarity].color}55;
    box-shadow: 0 0 10px ${p => RARITY[p.$rarity].glow};
    background-color: ${colors.bgColorBlack2};
    transition: transform 0.2s ease, box-shadow 0.2s ease;

    &:hover {
        transform: translateY(-5px) scale(1.02);
        box-shadow: 0 10px 28px ${p => RARITY[p.$rarity].glow};
    }
`;

const CardImage = styled.img`
    width: 100%;
    height: auto;
    display: block;
`;

const CardImagePlaceholder = styled.div`
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255,255,255,0.04);
    color: rgba(255,255,255,0.2);
    font-size: 12px;
`;

const RarityBadge = styled.span<{ $rarity: CardRarity }>`
    position: absolute;
    top: 6px;
    right: 6px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.4px;
    padding: 2px 7px;
    border-radius: 4px;
    color: #111;
    background-color: ${p => RARITY[p.$rarity].color};
    text-transform: uppercase;
`;

const CardFooter = styled.div`
    padding: 6px 8px;
    background-color: rgba(0,0,0,0.55);
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const FooterLine = styled.span`
    font-size: 10px;
    color: rgba(255,255,255,0.55);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const EmptyState = styled.div`
    width: 100%;
    padding: 32px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed rgba(255,255,255,0.12);
    border-radius: 10px;
    color: rgba(255,255,255,0.35);
    font-size: 14px;
    font-style: italic;
    letter-spacing: 0.3px;
`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function normalizeCardAuthor(card: Card): Card {
    if (!card.author) return card;
    const a = card.author;
    return {
        ...card,
        author_user: a.id
            ? { id: a.id, username: a.username ?? '', nickname_forum: a.nickname_forum ?? null }
            : null,
        author_name: !a.id && a.name ? a.name : (card.author_name ?? null),
    };
}

// ─── Component ───────────────────────────────────────────────────────────────

interface WrestlerCardsProps {
    cards: Card[];
    isLoading: boolean;
    isAuthenticated: boolean;
}

function WrestlerCards({ cards, isLoading, isAuthenticated }: WrestlerCardsProps) {
    const [selectedCard, setSelectedCard] = useState<Card | null>(null);

    return (
        <>
        <Section>
            <SectionHeader>
                <h2>Le sue carte</h2>
            </SectionHeader>

            {!isLoading && cards.length === 0 && (
                <EmptyState>Nessuna carta disponibile per questo wrestler</EmptyState>
            )}

            <Grid>
                {isLoading
                    ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
                    : cards.map((card, i) => {
                        const imgUrl = card.fronte_path
                            ? process.env.REACT_APP_STORAGE_URL + card.fronte_path
                            : null;

                        return (
                            <CardItem key={`${card.id}-${i}`} $rarity={card.rarity} onClick={() => setSelectedCard(normalizeCardAuthor(card))}>
                                {imgUrl
                                    ? <CardImage src={imgUrl} alt={`Card ${card.id}`} />
                                    : <CardImagePlaceholder>No image</CardImagePlaceholder>
                                }
                            </CardItem>
                        );
                    })
                }
            </Grid>
        </Section>

        {selectedCard && (
            <CardDetailModal
                card={selectedCard}
                ownedQuantity={0}
                isAuthenticated={isAuthenticated}
                onClose={() => setSelectedCard(null)}
            />
        )}
        </>
    );
}

export default WrestlerCards;
